import moment from "moment";
import React, { useEffect, useState } from "react";
import { Grid, TextField, Button, Card, CardContent, Typography, Box, Tabs, Tab, CircularProgress } from "@mui/material";
import MainDatatable from "../../components/datatable/MainDatatable.jsx";
import DatatableHeading from "../../components/datatable/DatatableHeading.jsx";
import { DeepSearchSpace } from "../../utils/common-function/index.js";
import { api_urls } from "../../utils/api-urls/index.js";

const AstrologyReports = () => {
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [consultationData, setConsultationData] = useState([]);
    const [astrologerEarnings, setAstrologerEarnings] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [dateRange, setDateRange] = useState({
        startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD')
    });
    const [summaryStats, setSummaryStats] = useState({
        totalConsultations: 0,
        totalEarnings: 0,
        totalChatMinutes: 0,
        totalCallMinutes: 0,
        totalVideoCallMinutes: 0
    });

    const filteredConsultations = DeepSearchSpace(consultationData, searchText);
    const filteredAstrologers = DeepSearchSpace(astrologerEarnings, searchText);

    const consultationColumns = [
        { name: 'S.No.', selector: (row, index) => index + 1, width: '70px' },
        { name: 'Date', selector: row => row?.createdAt ? moment(row?.createdAt).format('DD MMM YYYY HH:mm') : 'N/A', width: '160px' },
        { name: 'Customer', selector: row => row?.customerId?.name || row?.customerName || 'N/A', width: '140px' },
        { name: 'Astrologer', selector: row => row?.astrologerId?.astrologerName || row?.astrologerName || 'N/A', width: '140px' },
        { name: 'Type', selector: row => row?.type || 'Chat', width: '100px' },
        { name: 'Duration (min)', selector: row => row?.duration || 0, width: '120px' },
        { name: 'Rate/min', selector: row => '₹' + (row?.ratePerMinute || 0), width: '100px' },
        { name: 'Total Charge', selector: row => '₹' + (row?.totalCharge || 0).toFixed(2), width: '120px' },
        { name: 'Status', selector: row => row?.status || 'completed', width: '100px' },
    ];

    const astrologerColumns = [
        { name: 'S.No.', selector: (row, index) => index + 1, width: '70px' },
        { name: 'Astrologer', selector: row => row?.astrologerName || 'N/A', width: '160px' },
        { name: 'Total Consultations', selector: row => row?.totalConsultations || 0, width: '150px' },
        { name: 'Chat Sessions', selector: row => row?.chatCount || 0, width: '120px' },
        { name: 'Call Sessions', selector: row => row?.callCount || 0, width: '120px' },
        { name: 'Video Sessions', selector: row => row?.videoCallCount || 0, width: '120px' },
        { name: 'Total Minutes', selector: row => row?.totalMinutes || 0, width: '120px' },
        { name: 'Gross Earnings', selector: row => '₹' + (row?.grossEarnings || 0).toFixed(2), width: '130px' },
        { name: 'Commission', selector: row => '₹' + (row?.commission || 0).toFixed(2), width: '120px' },
        { name: 'Net Earnings', selector: row => '₹' + (row?.netEarnings || 0).toFixed(2), width: '130px' },
    ];

    const fetchConsultationReports = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${api_urls}api/consultation/get-consultation-reports?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`);
            const data = await response.json();
            if (data?.success) {
                setConsultationData(data?.consultations || []);
                setSummaryStats({
                    totalConsultations: data?.totalConsultations || 0,
                    totalEarnings: data?.totalEarnings || 0,
                    totalChatMinutes: data?.totalChatMinutes || 0,
                    totalCallMinutes: data?.totalCallMinutes || 0,
                    totalVideoCallMinutes: data?.totalVideoCallMinutes || 0
                });
            }
        } catch (error) {
            console.error('Error fetching consultation reports:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAstrologerEarnings = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${api_urls}api/consultation/get-astrologer-earnings-report?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`);
            const data = await response.json();
            if (data?.success) {
                setAstrologerEarnings(data?.astrologers || []);
            }
        } catch (error) {
            console.error('Error fetching astrologer earnings:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 0) {
            fetchConsultationReports();
        } else {
            fetchAstrologerEarnings();
        }
    }, [dateRange, activeTab]);

    const setQuickDateRange = (days) => {
        setDateRange({
            startDate: moment().subtract(days, 'days').format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD')
        });
    };

    return (
        <>
            <div style={{ padding: "20px", backgroundColor: "#fff", marginBottom: "20px", boxShadow: '0px 0px 5px lightgrey', borderRadius: "10px" }}>
                <DatatableHeading title="Astrology Reports" data={activeTab === 0 ? consultationData : astrologerEarnings} />
                
                {/* Summary Cards */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                    <div style={{ padding: '15px', backgroundColor: '#fff3e0', borderRadius: '8px', minWidth: '150px' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>Total Consultations</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f57c00' }}>{summaryStats.totalConsultations}</div>
                    </div>
                    <div style={{ padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '8px', minWidth: '150px' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>Total Earnings</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#388e3c' }}>₹{summaryStats.totalEarnings.toFixed(2)}</div>
                    </div>
                    <div style={{ padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px', minWidth: '150px' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>Chat Minutes</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#1976d2' }}>{summaryStats.totalChatMinutes}</div>
                    </div>
                    <div style={{ padding: '15px', backgroundColor: '#f3e5f5', borderRadius: '8px', minWidth: '150px' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>Call Minutes</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7b1fa2' }}>{summaryStats.totalCallMinutes}</div>
                    </div>
                    <div style={{ padding: '15px', backgroundColor: '#e0f7fa', borderRadius: '8px', minWidth: '150px' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>Video Call Minutes</div>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#00838f' }}>{summaryStats.totalVideoCallMinutes}</div>
                    </div>
                </div>

                {/* Quick Date Filters */}
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <Button variant="outlined" onClick={() => setQuickDateRange(1)}>Today</Button>
                    <Button variant="outlined" onClick={() => setQuickDateRange(7)}>Last 7 Days</Button>
                    <Button variant="outlined" onClick={() => setQuickDateRange(30)}>Last 30 Days</Button>
                    <Button variant="outlined" onClick={() => setQuickDateRange(90)}>Last 3 Months</Button>
                </div>

                {/* Date Range Inputs */}
                <div style={{ display: "flex", justifyContent: "space-between", gap: "20px", alignItems: 'center', marginBottom: "20px" }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <label>From:</label>
                        <input 
                            type='date' 
                            value={dateRange.startDate}
                            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                        <label>To:</label>
                        <input 
                            type='date' 
                            value={dateRange.endDate}
                            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                            style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                    </div>
                    <input 
                        type='text' 
                        placeholder='Search...'
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ padding: '10px', width: '250px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                </div>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}>
                    <Tabs value={activeTab} onChange={(e, newVal) => setActiveTab(newVal)}>
                        <Tab label="All Consultations" />
                        <Tab label="Astrologer Earnings" />
                    </Tabs>
                </Box>

                {/* Data Table */}
                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
                        <CircularProgress />
                    </div>
                ) : (
                    <MainDatatable 
                        columns={activeTab === 0 ? consultationColumns : astrologerColumns} 
                        data={activeTab === 0 ? filteredConsultations : filteredAstrologers} 
                    />
                )}
            </div>
        </>
    );
};

export default AstrologyReports;
